// pages/AccountPage.jsx
import React, { useEffect, useState, useRef } from "react";
import { useAuth } from "../AuthContext.jsx";
import {
  getMe,
  manageSubscription,
  updateMe,
  updatePassword,
  updateSubscription,
  // pulled in to mirror SearchPage analytics
  getClientSessionId,
  postAnalyticsImpressionsBatch,
} from "../api.js";

const ZIP_RE = /^\d{5}(?:-\d{4})?$/;

export default function AccountPage() {
  const { user, setUser, logout } = useAuth();

  const [email, setEmail] = useState(user?.email || "");
  const [sensitivity, setSensitivity] = useState(user?.sensitivity ?? 5);
  const [homeZip, setHomeZip] = useState(user?.home_zip ?? "");
  const [zipDirty, setZipDirty] = useState(false);

  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState("");
  const [err, setErr] = useState("");

  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [pwMsg, setPwMsg] = useState("");
  const [pwErr, setPwErr] = useState("");

  // Modal state for "coming soon"
  const [showSubscribeSoon, setShowSubscribeSoon] = useState(false);

  // Keep user state in sync on mount
  useEffect(() => {
    getMe()
      .then(({ user }) => {
        setUser(user || null);
        if (user) {
          setEmail(user.email);
          setSensitivity(user.sensitivity ?? 5);
          setHomeZip(user.home_zip ?? "");
        }
      })
      .catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /* ---------------- analytics: queue (mirrors SearchPage pattern) ---------------- */
  const clientSessionIdRef = useRef(getClientSessionId());
  const impressionQueueRef = useRef([]);
  const flushTimerRef = useRef(null);
  const loggedThisMinuteRef = useRef(new Map()); // `${event}:${minuteIso}` -> true

  function enqueueAccountAnalytics(rows) {
    if (!rows?.length) return;
    impressionQueueRef.current.push(...rows);
    scheduleFlush();
  }

  function scheduleFlush() {
    if (flushTimerRef.current) return;
    flushTimerRef.current = setTimeout(() => flushImpressions(false), 800);
  }

  async function flushImpressions(forceBeacon = false) {
    const q = impressionQueueRef.current;
    impressionQueueRef.current = [];
    clearTimeout(flushTimerRef.current);
    flushTimerRef.current = null;
    if (!q.length) return;

    const payload = {
      client_session_id: clientSessionIdRef.current,
      search_id: null, // not a search page
      plan_hint: user?.plan ?? undefined,
      results: q,
    };

    try {
      if (forceBeacon) {
        const data = JSON.stringify(payload);
        let sent = false;
        try {
          if (typeof Blob !== "undefined") {
            const blob = new Blob([data], { type: "application/json" });
            sent = navigator.sendBeacon?.("/api/v1/analytics/impressions", blob);
          }
        } catch {}
        if (!sent) {
          try {
            if (typeof TextEncoder !== "undefined") {
              const buf = new TextEncoder().encode(data);
              sent = navigator.sendBeacon?.("/api/v1/analytics/impressions", buf);
            }
          } catch {}
        }
        if (!sent) {
          try {
            sent = navigator.sendBeacon?.("/api/v1/analytics/impressions", data);
          } catch {}
        }
      } else {
        await postAnalyticsImpressionsBatch(payload);
      }
    } catch {
      // swallow; server has per-minute dedupe anyway
    }
  }

  useEffect(() => {
    const onHide = () => flushImpressions(true);
    const onUnload = () => flushImpressions(true);
    document.addEventListener("visibilitychange", onHide);
    window.addEventListener("beforeunload", onUnload);
    return () => {
      document.removeEventListener("visibilitychange", onHide);
      window.removeEventListener("beforeunload", onUnload);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.plan]);

  /* ---------------- profile update handlers ---------------- */
  const onSaveProfile = async (e) => {
    e.preventDefault();
    setSaving(true);
    setMsg("");
    setErr("");

    const trimmedZip = (homeZip || "").trim();
    if (trimmedZip && !ZIP_RE.test(trimmedZip)) {
      setZipDirty(true);
      setSaving(false);
      return setErr("Please enter a valid ZIP code (12345 or 12345-6789).");
    }

    try {
      const { user: updated } = await updateMe({
        email,
        sensitivity,
        home_zip: trimmedZip === "" ? null : trimmedZip,
      });
      setUser(updated);
      setMsg("Profile updated.");
    } catch (e) {
      setErr(e.message || "Failed to update");
    } finally {
      setSaving(false);
    }
  };

  const onChangePassword = async (e) => {
    e.preventDefault();
    setPwMsg("");
    setPwErr("");
    try {
      await updatePassword({
        current_password: currentPassword,
        new_password: newPassword,
      });
      setPwMsg("Password changed.");
      setCurrentPassword("");
      setNewPassword("");
    } catch (e) {
      setPwErr(e.message || "Failed to change password");
    }
  };

  /* ---------------- subscription handlers ---------------- */
  // Original flow (kept intact for easy revert)
  const onSubscribe = async (e) => {
    const { url } = await updateSubscription();
    window.location.href = url;
  };

  // Temporary flow: show modal + log the click using the same analytics pipeline
  const onSubscribeSoon = (e) => {
    e.preventDefault();
    setShowSubscribeSoon(true);

    const minuteIso = new Date();
    minuteIso.setSeconds(0, 0);
    const minuteKey = minuteIso.toISOString();
    const key = `subscribe_cta_click:${minuteKey}`;
    if (!loggedThisMinuteRef.current.get(key)) {
      loggedThisMinuteRef.current.set(key, true);
      enqueueAccountAnalytics([
        {
          event: "subscribe_cta_click",
          page: "account",
          plan: user?.plan ?? null,
          shown_at: new Date().toISOString(),
        },
      ]);
    }
  };

  const onManageSubscription = async (e) => {
    const { url } = await manageSubscription();
    window.location.href = url;
  };

  if (!user) return null;

  const zipInvalid = zipDirty && homeZip && !ZIP_RE.test(homeZip);

  return (
    <div className="panel narrow">
      <h2>Account</h2>

      <h3>Plan</h3>
      {user.plan !== "pro" && (
        <div className="notice info" style={{ marginTop: 18, marginBottom: 18 }}>
          <strong>Free plan with limited results.</strong> Subscribe to unlock full results.
        </div>
      )}
      {user.plan !== "pro" && (
        // Swap back to onSubscribe when real flow is ready
        <button onClick={onSubscribeSoon} type="button" className="btn btn-primary">
          Subscribe
        </button>
      )}
      {user.plan === "pro" && (
        <div className="notice info" style={{ marginTop: 18, marginBottom: 18 }}>
          <strong>Full access plan with no limitations.</strong> Thank you for supporting Where's Gluten Free.
        </div>
      )}
      {user.plan === "pro" && (
        <button onClick={onManageSubscription} type="button" className="btn btn-primary">
          Manage
        </button>
      )}

      <form onSubmit={onSaveProfile} className="form" style={{ marginTop: 18, marginBottom: 24 }}>
        <h3>Profile</h3>
        {msg && <div className="hint" style={{ color: "green" }}>{msg}</div>}
        {err && <div className="error">{err}</div>}

        <label className="field">
          <span>Email</span>
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            autoComplete="email"
            required
          />
        </label>

        {/* Home ZIP code (optional) */}
        <label className="field">
          <span>Home ZIP code (optional)</span>
          <span>We use this to make sure we support your area.</span>
          <input
            type="text"
            inputMode="numeric"
            autoComplete="postal-code"
            placeholder="e.g. 12345 or 12345-6789"
            value={homeZip}
            onChange={(e) => setHomeZip(e.target.value)}
            onBlur={() => setZipDirty(true)}
            aria-invalid={zipInvalid ? "true" : "false"}
            aria-describedby="zip-help"
            pattern="\d{5}(-\d{4})?"
          />
          {zipInvalid && <div className="field-error">Invalid ZIP format.</div>}
        </label>

        {/* Sensitivity as 11 buttons (0..10) */}
        <div className="field sensitivity-field" style={{ marginTop: 6, marginBottom: 6 }}>
          <span>Gluten sensitivity: {sensitivity}</span>
          <span>0 - Can eat gluten</span>
          <span>5 - Ok with a little cross-contamination</span>
          <span>10 - No cross-contamination allowed</span>

          <div
            className="segmented sensitivity-grid"
            style={{ marginTop: 6 }}
            role="group"
            aria-label="Gluten sensitivity from 0 to 10"
          >
            {Array.from({ length: 11 }, (_, n) => n).map((n) => (
              <button
                key={n}
                type="button"
                className={`seg ${sensitivity === n ? "selected" : ""}`}
                aria-pressed={sensitivity === n ? "true" : "false"}
                onClick={() => setSensitivity(n)}
                title={`Sensitivity ${n}`}
              >
                {n}
              </button>
            ))}
          </div>
        </div>

        <button
          type="submit"
          className="btn btn-primary"
          disabled={saving}
          style={{ marginRight: "auto" }}
        >
          {saving ? "Saving…" : "Save changes"}
        </button>
      </form>

      <form onSubmit={onChangePassword} className="form" style={{ marginBottom: 24 }}>
        <h3>Change password</h3>
        {pwMsg && <div className="hint" style={{ color: "green" }}>{pwMsg}</div>}
        {pwErr && <div className="error">{pwErr}</div>}

        <label className="field">
          <span>Current password</span>
          <input
            type="password"
            value={currentPassword}
            onChange={(e) => setCurrentPassword(e.target.value)}
            autoComplete="current-password"
            required
          />
        </label>

        <label className="field">
          <span>New password</span>
          <input
            type="password"
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
            autoComplete="new-password"
            required
          />
        </label>

        <button type="submit" className="btn btn-primary" style={{ marginRight: "auto" }}>
          Change password
        </button>
      </form>

      {/* “Coming soon” modal for Subscribe */}
      {showSubscribeSoon && (
        <div
          role="dialog"
          aria-modal="true"
          aria-labelledby="subsoon-title"
          className="modal-backdrop"
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(0,0,0,0.45)",
            display: "grid",
            placeItems: "center",
            zIndex: 1000,
          }}
          onClick={() => setShowSubscribeSoon(false)}
        >
          <div
            className="modal"
            style={{
              background: "white",
              borderRadius: 10,
              padding: 16,
              width: "min(420px, 90vw)",
              boxShadow: "0 8px 30px rgba(0,0,0,0.25)",
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <h3 id="subsoon-title" style={{ marginTop: 0 }}>Subscription coming soon</h3>
            <p style={{ marginBottom: 16 }}>
              Thanks for your interest! Subscriptions aren’t available just yet. Check back shortly.
            </p>
            <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
              <button
                type="button"
                className="btn"
                onClick={() => setShowSubscribeSoon(false)}
                aria-label="Close"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      <button onClick={logout} className="btn btn-danger">
        Log out
      </button>
    </div>
  );
}
